// What the account assistant sees of the product creator (storefront/create-a-product.vue):
// a plain, size-capped summary of the wizard's state, sent with each chat message so the
// assistant can advise on the product being made (PRO-3491, phase 1: it reads, never writes).
// Prices are the creator's own numbers (pricingFromCut), so the assistant's arithmetic starts
// from what the seller sees.

import { calculateArtworkGeometry } from "@/lib/geometry.js";
import { customPlacements, getCustomSize, placementToBoxMap } from "@/lib/product-creator.js";
import { changeOptions } from "@/lib/creator-assistant-changes.js";

// Kept well under the server's cap (AssistantController): the colours a seller can choose are
// limited anyway, and each sends only the items they've included.
// The most colours any account may choose (the higher tiers' limit).
const MAX_COLORS = 20;
const MAX_ITEMS_PER_COLOR = 8;
// The creator's own limits (title 85, description 2000), so `expect` can match the field exactly.
const MAX_TEXT = 2000;

export const money = (value) => (value === null || value === undefined ? null : Number(value).toFixed(2));
const text = (value) => (typeof value === "string" ? value.slice(0, MAX_TEXT) : null);

export function itemName(item) {
  // As the colour panel shows it ("{brand} {name}", with the code the creator lists it by).
  const blank = item.blank?.name ? [item.blank.brand, item.blank.name, item.blank.code ? `(${item.blank.code})` : null].filter(Boolean).join(" ") : null;

  return blank || item.name || [item.style, item.finish].filter(Boolean).join(" ") || item.code || null;
}

/** A chosen colour as the page names it: a swatch name, a hex colour, or a sticker's size. */
export function colorName(color) {
  return color.name ?? color.size ?? color.bundle?.size ?? null;
}

/**
 * The page's price boxes (itemsGroupedForPricing): one box per group of items that share a price,
 * shown once something in it is chosen. Typing into a box sets its price, so a box is what
 * set_price changes.
 */
export function priceBoxes(vm) {
  return (vm.itemsGroupedForPricing ?? []).flatMap((group) =>
    (group.priceGroups ?? [])
      .filter((priceGroup) => priceGroup.displayItem && priceGroup.items.some((item) => item.isIncludedAndValid ?? item.isIncluded))
      .map((priceGroup) => ({ priceGroup, box: group.name && group.name !== priceGroup.label ? `${group.name} · ${priceGroup.label}` : String(priceGroup.label) })),
  );
}

const fileOf = (pathInfo) => (pathInfo ? { dirname: pathInfo.dirname, basename: pathInfo.basename, extension: pathInfo.extension } : null);

/**
 * The box a placement fits the artwork into, in inches: a custom placement's own size, or the
 * named placement's (medium is 7in wide, large 9in…). The creator only keeps printGeometry up to
 * date for custom placements, so a named one reads its box from the placement.
 */
export function placementBox(placement, printGeometry) {
  if (!customPlacements.includes(placement) && placementToBoxMap[placement]) {
    return placementToBoxMap[placement];
  }

  return printGeometry?.width && printGeometry?.height ? { width: Number(printGeometry.width), height: Number(printGeometry.height) } : null;
}

function artwork(vm, rasterGeometry, printGeometry, pathInfo, uploadMeta, sourcePathInfo, placement) {
  // Before an upload the creator holds a 1 × 1 placeholder size.
  if (!(rasterGeometry?.width > 1) || !(rasterGeometry?.height > 1) || !pathInfo) {
    return { uploaded: !!pathInfo };
  }

  const box = placementBox(placement, printGeometry);
  const printed = box ? calculateArtworkGeometry(1, box, rasterGeometry) : null;
  const widthInches = printed ? Number(printed.artworkWidthInches) : null;

  return {
    uploaded: true,
    pixels: `${rasterGeometry.width} × ${rasterGeometry.height}`,
    printedInches: printed ? `${widthInches.toFixed(1)} × ${Number(printed.artworkHeightInches).toFixed(1)}` : null,
    // Pixels per inch at the printed size: what decides whether it prints sharp.
    pixelsPerInch: widthInches ? Math.round(Number(rasterGeometry.width) / widthInches) : null,
    // The staged upload on the server, for view_artwork and edit_artwork (a random name), and
    // the untrimmed copy behind "Preserve artwork padding", which an edit starts from.
    file: fileOf(pathInfo),
    trimmedFile: fileOf(uploadMeta?.pathInfo),
    untrimmedFile: fileOf(uploadMeta?.untrimmed?.pathInfo),
    // A screen print's colour-separated source file is what gets printed.
    sourceFileAttached: !!sourcePathInfo,
  };
}

// The items this product type can be made on, each with its base price as the creator would
// set it now (basePriceFor: method, inks and the seller's own adjustment included), so pricing
// questions can be answered before any colour is picked. One entry per item, not per colour.
const MAX_OFFERED = 30;

function basePrice(vm, item) {
  try {
    return money(vm.basePriceFor(item));
  } catch {
    // An item the pricing can't read is listed without a price.
    return null;
  }
}

function offeredItems(vm) {
  const input = vm.input;
  const types = vm.productTypes;
  const seen = new Map();
  const offer = (name, item, extra = {}) => {
    if (name && !seen.has(name) && seen.size < MAX_OFFERED) {
      seen.set(name, { item: name, base: basePrice(vm, item), ...extra });
    }
  };

  if (input.productType === types.PHONECASE) {
    // Priced by case style (MagSafe or not), for every enabled phone model.
    const cases = (vm.phoneCases ?? []).filter((item) => item.enabled);

    for (const item of cases) {
      const style = item.magsafe ? `${item.style} MagSafe` : item.style;
      offer(`${style} case`, item, { phoneModels: cases.filter((other) => other.style === item.style && !!other.magsafe === !!item.magsafe).length });
    }
  } else if (input.productType === types.DRINKWARE) {
    for (const drink of vm.drinkware ?? []) {
      for (const blank of drink.blanks ?? []) {
        offer(`${drink.name} (${blank.code})`, { name: drink.name, code: blank.code });
      }
    }
  } else if (input.productType === types.STICKER) {
    for (const bundle of vm.stickers?.bySize ?? []) {
      offer(`${bundle.name} sticker`, { size: bundle.size });
    }
  } else {
    const lists = {
      [types.APPAREL]: vm.bundles,
      [types.HAT]: vm.hatBundles,
      [types.BEANIE]: vm.beanieBundles,
      [types.TOTE]: vm.toteBundles,
    };

    for (const bundle of lists[input.productType] ?? []) {
      for (const item of bundle.items ?? []) {
        if ((item.sales_method && item.sales_method !== input.salesMethod) || (item.production_method && item.production_method !== input.productionMethod)) {
          continue;
        }

        offer(itemName(item), item, { sizes: item.sizeRanges?.[input.productionMethod] ?? null });
      }
    }
  }

  return [...seen.values()];
}

/** The print methods the page's Decoration Method panel offers (shirts only), or null. */
export function printMethods(vm) {
  if (vm.input.productType !== vm.productTypes.APPAREL) {
    return null;
  }

  const methods = vm.productionMethods;

  return vm.input.salesMethod === vm.salesMethods.CAMPAIGN ? [methods.SCREENPRINT, methods.EMBROIDERY] : [methods.DIRECTTOGARMENT, methods.EMBROIDERY];
}

/** The snapshot for one message. `vm` is the creator page's component instance. */
export function creatorSnapshot(vm) {
  const input = vm.input;
  const isScreenPrint = input.productionMethod === vm.productionMethods.SCREENPRINT;

  const colors = input.colors.slice(0, MAX_COLORS).map((color) => {
    const items = (color.bundle?.items ?? []).filter((item) => item.isIncludedAndValid ?? item.isIncluded);

    return {
      color: colorName(color),
      onScreen: !!color.isActive,
      itemCount: items.length,
      items: items.slice(0, MAX_ITEMS_PER_COLOR).map(itemName),
    };
  });

  // base + bonus adjustment + cut = retail; the seller earns cut + bonus per sale.
  const prices = priceBoxes(vm).map(({ priceGroup, box }) => {
    const pricing = priceGroup.displayItem.pricing ?? {};

    return {
      box,
      items: [...new Set(priceGroup.items.filter((item) => item.isIncludedAndValid ?? item.isIncluded).map(itemName))].slice(0, 6),
      base: money(pricing.base),
      cut: money(pricing.cut),
      bonus: money(pricing.bonus),
      bonusPriceAdjustment: money(pricing.bonusPriceAdjustment),
      retail: money(pricing.retail),
      earnsPerSale: money(pricing.cutPlusBonus),
      lowest: money(priceGroup.displayItem.min),
      highest: money(priceGroup.displayItem.max),
    };
  });

  return {
    page: "product creator",
    step: vm.step,
    isCustomFlow: !!vm.isCustomFlow,
    signedIn: !!vm.user,
    // Pro bono: the seller has opted out of payment, so their cut is always $0.
    proBono: !!vm.c_isProBono,
    productType: input.productType,
    salesMethod: input.salesMethod,
    productionMethod: input.productionMethod,
    placement: input.placement,
    // The box the placement fits the artwork into (custom: the seller's own size), and for a
    // custom placement how far the art is nudged from where it sits (inches; y above 0 is up)
    // and the limits the page's size and offset inputs allow.
    printArea: (() => {
      const box = placementBox(input.placement, input.printGeometry);
      return box ? `${box.width} × ${box.height} in` : null;
    })(),
    customPlacement: customPlacements.includes(input.placement)
      ? (() => {
          const size = getCustomSize(input.productType, input.placement);
          return {
            offset: { x: Number(input.printOffset?.x ?? 0), y: Number(input.printOffset?.y ?? 0) },
            offsetRange: vm.offsetRange ?? null,
            sizeRange: { min: size.min, max: size.max },
          };
        })()
      : null,
    screenPrint: isScreenPrint ? { inks: input.numInks, inkType: input.inkType, inkEffect: input.inkEffect, sourceFileUploaded: !!input.artwork?.sourceArtworkPathInfo } : null,
    // A campaign's dates, with the bounds the page's own inputs enforce (local time, `timeZone`).
    campaign:
      input.salesMethod === vm.salesMethods.CAMPAIGN
        ? {
            start: input.campaignStart,
            end: input.campaignEnd,
            earliestStart: vm.campaignStartMin ?? null,
            earliestEnd: vm.campaignEndMin ?? null,
            latestEnd: vm.campaignEndMax ?? null,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }
        : null,
    // The print methods the page offers this product (shirts only), and screen print's ink choices.
    printMethods: printMethods(vm),
    inkOptions: isScreenPrint ? { inks: [1, 2, 3, 4, 5, 6, 7, 8], inkTypes: Object.keys(vm.inkTypes ?? {}), inkEffects: Object.keys(vm.inkEffects ?? {}) } : null,
    artwork: artwork(vm, vm.rasterGeometry, input.printGeometry, input.artwork?.rasterArtworkPathInfo, vm.originalUploadMeta, input.artwork?.sourceArtworkPathInfo, input.placement),
    secondLocation: input.secondLocation
      ? {
          placement: input.secondLocation.placement ?? null,
          artwork: artwork(
            vm,
            vm.secondRasterGeometry,
            input.secondLocation.printGeometry,
            input.secondLocation.artwork?.rasterArtworkPathInfo,
            vm.secondOriginalUploadMeta,
            input.secondLocation.artwork?.sourceArtworkPathInfo,
            input.secondLocation.placement,
          ),
        }
      : null,
    colors,
    colorsChosen: input.colors.length,
    // One per price box on the page.
    prices,
    // Every item this product type can use, with its base price, chosen or not.
    offeredItems: offeredItems(vm),
    // What change_creator can pick from: colours (or sticker sizes) that can be added, and placements.
    ...changeOptions(vm),
    defaultCut: money(vm.baseProfit),
    // On (the default), changing one price moves every price by the same amount.
    synchronizedPrices: !!vm.synchronizePrices,
    title: text(input.meta?.title),
    description: text(input.meta?.description),
    // What happens on Submit, as the page decides it.
    launch: {
      needsReview: !!vm.requiresApproval,
      launchesOnSubmit: !!vm.willLaunchAfterSubmission,
      accountReady: !vm.accountIncomplete,
      accountMissing: vm.user?.readiness?.isReadyForLaunch ? null : (vm.user?.readiness?.reason?.user_readable ?? null),
      // The "publish as soon as it's ready" choice only shows when it can matter (Details, Advanced Settings).
      publishChoiceShown: !!(vm.willLaunchAfterSubmission || vm.isEodSubmission),
      publishWhenReady: input.shouldPublish?.id === "yes",
    },
  };
}
